import { useCallback, useEffect, useState } from "react";

const HISTORY_KEY = "17c_history";
const MAX_HISTORY = 20;

export interface HistoryEntry {
  id: string;
  viewedAt: number;
}

function readHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as HistoryEntry[]) : [];
  } catch {
    return [];
  }
}

export function useHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    setHistory(readHistory());
    const onStorage = (e: StorageEvent) => {
      if (e.key === HISTORY_KEY) setHistory(readHistory());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addHistory = useCallback((id: string) => {
    const current = readHistory().filter((entry) => entry.id !== id);
    const next = [{ id, viewedAt: Date.now() }, ...current].slice(0, MAX_HISTORY);
    setHistory(next);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota errors */
    }
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      localStorage.removeItem(HISTORY_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return { history, addHistory, clearHistory };
}